import React, { useState, KeyboardEvent, ChangeEvent } from 'react';
import { FiX } from 'react-icons/fi';
import Icon from '../Icon';

interface ChipInputProps {
    value: string[];
    onChange: (value: string[]) => void;
    placeholder?: string;
    label?: string;
    className?: string;
}

export default function ChipInput({
    value,
    onChange,
    placeholder = 'Add and press Enter...',
    label,
    className = ''
}: ChipInputProps) {
    const [inputValue, setInputValue] = useState('');

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            const trimmed = inputValue.trim().replace(/,$/, '');
            if (trimmed && !value.includes(trimmed)) {
                onChange([...value, trimmed]);
                setInputValue('');
            }
        } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
            onChange(value.slice(0, -1));
        }
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const removeChip = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
    };

    return (
        <div className={`w-full ${className}`}>
            {label && (
                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                    {label}
                </label>
            )}
            <div className={[
                'flex flex-wrap gap-2 rounded-xl border p-2 text-sm transition min-h-[48px]',
                'bg-white text-gray-900 border-gray-300 shadow-sm focus-within:border-blue-500',
                'dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 dark:shadow-none'
            ].join(' ')}>
                {value.map((chip, index) => (
                    <span
                        key={`${chip}-${index}`}
                        className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                    >
                        {chip}
                        <button
                            type="button"
                            onClick={() => removeChip(index)}
                            className="hover:text-blue-900 dark:hover:text-blue-100 focus:outline-none"
                        >
                            <Icon icon={FiX} className="h-3 w-3" />
                        </button>
                    </span>
                ))}
                <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={value.length === 0 ? placeholder : ''}
                    className="flex-1 bg-transparent outline-none placeholder:text-gray-400 min-w-[120px]"
                />
            </div>
        </div>
    );
}
